import apiClient from './apiClient';

const vendorService = {
  // Get all vendors with pagination and filtering
  getAll: async (params = {}) => {
    const response = await apiClient.get('/vendors', { params });
    return response.data;
  },

  // Get vendor by ID
  getById: async (id) => {
    const response = await apiClient.get(`/vendors/${id}`);
    return response.data;
  },

  // Get only active vendors
  getActive: async () => {
    const response = await apiClient.get('/vendors', {
      params: {
        status: 'active',
        verified: 'true'
      }
    });
    return response.data;
  },

  // Create new vendor (admin only)
  create: async (vendorData) => {
    // Accept either plain object or FormData
    if (vendorData instanceof FormData) {
      const response = await apiClient.post('/vendors', vendorData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }

    // If object contains File objects (from form), convert to FormData
    if (vendorData && (vendorData.logoFile || vendorData.coverFile)) {
      const fd = new FormData();
      Object.keys(vendorData).forEach((key) => {
        const val = vendorData[key];
        if (val === undefined || val === null) return;
        if (key === 'logoFile' || key === 'coverFile') return;
        if (Array.isArray(val) || typeof val === 'object') {
          fd.append(key, JSON.stringify(val));
        } else {
          fd.append(key, val);
        }
      });
      if (vendorData.logoFile) fd.append('logo', vendorData.logoFile);
      if (vendorData.coverFile) fd.append('cover', vendorData.coverFile);
      const response = await apiClient.post('/vendors', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data;
    }

    const response = await apiClient.post('/vendors', vendorData);
    return response.data;
  },

  // Refer a company/vendor
  refer: async (companyData) => {
    const response = await apiClient.post('/vendors/refer', companyData);
    return response.data;
  },

  // Update vendor
  update: async (id, vendorData) => {
    if (vendorData instanceof FormData) {
      const response = await apiClient.put(`/vendors/${id}`, vendorData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }

    if (vendorData && (vendorData.logoFile || vendorData.coverFile)) {
      const fd = new FormData();
      Object.keys(vendorData).forEach((key) => {
        const val = vendorData[key];
        if (val === undefined || val === null) return;
        if (key === 'logoFile' || key === 'coverFile') return;
        if (Array.isArray(val) || typeof val === 'object') {
          fd.append(key, JSON.stringify(val));
        } else {
          fd.append(key, val);
        }
      });
      if (vendorData.logoFile) fd.append('logo', vendorData.logoFile);
      if (vendorData.coverFile) fd.append('cover', vendorData.coverFile);
      const response = await apiClient.put(`/vendors/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data;
    }

    const response = await apiClient.put(`/vendors/${id}`, vendorData);
    return response.data;
  },

  // Delete vendor (admin only)
  delete: async (id) => {
    const response = await apiClient.delete(`/vendors/${id}`);
    return response.data;
  },

  // Verify vendor (admin only)
  verify: async (id) => {
    const response = await apiClient.post(`/vendors/${id}/verify`);
    return response.data;
  },

  // Unverify vendor (admin only)
  unverify: async (id) => {
    const response = await apiClient.post(`/vendors/${id}/unverify`);
    return response.data;
  },

  // Claim vendor ownership
  claim: async (id) => {
    const response = await apiClient.post(`/vendors/${id}/claim`);
    return response.data;
  },

  // Get vendor employees
  getEmployees: async (id) => {
    const response = await apiClient.get(`/vendors/${id}/employees`);
    return response.data;
  },

  // Add employee to vendor
  addEmployee: async (id, employeeData) => {
    const response = await apiClient.post(`/vendors/${id}/employees`, employeeData);
    return response.data;
  },

  // Update employee role
  updateEmployeeRole: async (vendorId, userId, roleData) => {
    const response = await apiClient.put(`/vendors/${vendorId}/employees/${userId}`, roleData);
    return response.data;
  },

  // Remove employee from vendor
  removeEmployee: async (vendorId, userId) => {
    const response = await apiClient.delete(`/vendors/${vendorId}/employees/${userId}`);
    return response.data;
  },

  // Get vendor products
  getProducts: async (id, params = {}) => {
    const response = await apiClient.get(`/vendors/${id}/products`, { params });
    return response.data;
  },

  // Get pending vendors (admin only)
  getPending: async () => {
    const response = await apiClient.get('/vendors/pending');
    return response.data;
  },

  // Search vendors
  search: async (query) => {
    const response = await apiClient.get('/vendors/search', {
      params: { q: query }
    });
    return response.data;
  },

  // Get vendors for select controls (admin or owned/employee vendors for sellers)
  getForSelect: async (params = {}) => {
    const response = await apiClient.get('/vendors/select', { params });
    return response.data;
  },

  // Get vendors owned by current user
  getMyVendors: async (params = {}) => {
    const response = await apiClient.get('/vendors/my', { params });
    return response.data;
  },

  // Get vendors where current user is an employee
  getEmployeeVendors: async (params = {}) => {
    const response = await apiClient.get('/vendors/employee-of', { params });
    return response.data;
  }
};

export default vendorService;
